import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Sparkles, UserRound } from "lucide-react";
import { api, formatApiError } from "../../../lib/api";
import { Panel, GhostBtn } from "./ui";
import { ConfirmAssign } from "./ConfirmAssign";

const TONES = ["bg-sky-500/70 border-sky-300/60", "bg-violet-500/60 border-violet-300/60", "bg-emerald-500/60 border-emerald-300/60", "bg-indigo-500/60 border-indigo-300/60"];
const hh = (m) => `${String(Math.floor(m / 60) % 24).padStart(2, "0")}:00`;

export function Planning({ plan, nowMin, onReload }) {
  const [pick, setPick] = useState(null);
  const lines = plan?.lines || [];
  const sugg = plan?.suggestions || [];
  const [from, to] = useMemo(() => {
    const all = [...lines.flatMap(l => l.blocks), ...sugg];
    const s = Math.min(8 * 60, ...all.map(b => b.start)), e = Math.max(20 * 60, ...all.map(b => b.end));
    return [Math.floor(s / 120) * 120, Math.min(Math.ceil(e / 120) * 120, 24 * 60)];
  }, [lines, sugg]);
  const pos = (m) => `${((Math.max(from, Math.min(m, to)) - from) / (to - from)) * 100}%`;
  const ticks = []; for (let m = from; m <= to; m += 120) ticks.push(m);
  const byDriver = (id) => sugg.filter(s => s.driver_id === id);
  const apply = async (s) => {
    try {
      const body = { driver_id: s.driver_id, ...(s.vehicle_id ? { vehicle_id: s.vehicle_id } : {}) };
      await api.patch(`/bookings/${s.booking_id}`, body);
      toast.success("Assegnazione applicata");
      setPick(null); onReload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const Bar = ({ l, s, e, cls, children, onClick, testid, half }) => (
    <button type="button" onClick={onClick} data-testid={testid} style={{ left: pos(s), width: `max(34px, calc(${pos(e)} - ${pos(s)} - 3px))` }}
      className={`absolute ${half === 1 ? "top-1 h-[calc(50%-3px)]" : half === 2 ? "bottom-1 h-[calc(50%-3px)]" : "top-1 bottom-1"} rounded-md border px-1.5 text-left overflow-hidden text-[10.5px] leading-tight text-white ${cls}`} title={l}>{children}</button>
  );
  return (
    <Panel title="Pianificazione intelligente" badge="AI" testid="nx-planning" className="scroll-mt-24"
      right={<GhostBtn onClick={onReload} testid="nx-plan-refresh">Rigenera piano</GhostBtn>} bodyClass="px-3 pb-3">
      <div id="nx-planning" className="overflow-x-auto">
        <div className="min-w-[520px]">
          <div className="relative h-5 ml-[92px] border-b border-white/10">
            {ticks.map(t => <span key={t} className="absolute -translate-x-1/2 text-[10px] text-slate-400 font-mono-num" style={{ left: pos(t) }}>{hh(t)}</span>)}
          </div>
          <div className="max-h-[270px] overflow-y-auto">
            {lines.map((l, i) => (
              <div key={l.driver_id} className="flex items-stretch h-11 border-b border-white/5" data-testid={`nx-plan-line-${l.driver_id}`}>
                <div className="w-[92px] shrink-0 flex items-center gap-1.5 pr-1">
                  <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center shrink-0"><UserRound size={13} className="text-slate-300" /></span>
                  <span className="min-w-0 leading-tight"><span className="block text-[11px] text-white truncate">{l.name}</span><span className="block text-[9.5px] text-slate-500 truncate">{l.plate || (l.shift === "NIGHT" ? "Notte" : l.shift === "DAY" ? "Giorno" : "—")}</span></span>
                </div>
                <div className="relative flex-1">
                  {nowMin >= from && nowMin <= to && <span className="absolute top-0 bottom-0 w-px bg-sky-400/60" style={{ left: pos(nowMin) }} />}
                  {l.blocks.map(b => (
                    <Bar key={b.id} l={`${b.time} ${b.label}`} s={b.start} e={b.end} testid={`nx-plan-block-${b.id}`}
                      half={b.conflict ? 2 : l.blocks.some(o => o.conflict && o.id !== b.id && o.start < b.end && b.start < o.end) ? 1 : 0} onClick={() => window.location.assign(`/prenotazioni?open=${b.id}`)}
                      cls={b.conflict ? "bg-rose-600/50 border-rose-400 border-dashed" : TONES[i % TONES.length]}>{b.time}<br />{b.conflict ? "Conflitto" : b.label}</Bar>
                  ))}
                  {byDriver(l.driver_id).map(s => (
                    <Bar key={`s-${s.booking_id}`} l="Proposta" s={s.start} e={s.end} testid={`nx-plan-sugg-${s.booking_id}`} onClick={() => setPick(s)}
                      cls="bg-[#2A2414] border-[#E9C46A] border-dashed text-[#F1D27A]"><Sparkles size={10} className="inline mr-0.5" />{s.time}<br />Proposta</Bar>
                  ))}
                </div>
              </div>
            ))}
            {!lines.length && <div className="text-sm text-slate-500 text-center py-10" data-testid="nx-plan-empty">Nessun autista di turno o servizio assegnato oggi</div>}
          </div>
          {sugg.some(s => !s.driver_id) && (
            <div className="mt-2 text-[11px] text-amber-300" data-testid="nx-plan-nodriver">{sugg.filter(s => !s.driver_id).length} servizi da assegnare senza autista di turno libero: {sugg.filter(s => !s.driver_id).map(s => s.time).join(", ")}</div>
          )}
        </div>
      </div>
      {pick && <ConfirmAssign s={pick} onCancel={() => setPick(null)} onConfirm={() => apply(pick)} />}
    </Panel>
  );
}
